import { useEffect, useState } from "react";
import { Archive, Eye, FileText, Globe, PencilLine, Plus } from "lucide-react";
import { toast } from "sonner";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { EmptyState } from "../../components/shared/EmptyState";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { api, formatApiError } from "../../lib/api";

const STATUS_LABELS = { brouillon: "Brouillon", publie: "Publiée", archive: "Archivée" };
const EMPTY = { version: "", title: "Conditions Générales de Vente", effective_at: new Date().toISOString().slice(0, 10), content: "" };

export default function TermsAdminPage() {
  const [terms, setTerms] = useState(null);
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [acceptances, setAcceptances] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/terms").then((r) => setTerms(r.data)).catch(() => setTerms([]));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (term) => {
    setEditing(term);
    setForm({ version: term.version, title: term.title, effective_at: term.effective_at, content: term.content });
    setOpen(true);
  };
  const openView = async (term) => {
    setViewing(term);
    try {
      const { data } = await api.get(`/admin/terms/${term.id}/acceptances`);
      setAcceptances(data);
    } catch { setAcceptances([]); }
  };

  const submit = async () => {
    try {
      if (editing) {
        await api.patch(`/admin/terms/${editing.id}`, form);
        toast.success("Brouillon CGV mis à jour");
      } else {
        await api.post("/admin/terms", form);
        toast.success("Nouvelle version CGV créée en brouillon");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const publish = async (term) => {
    try {
      await api.post(`/admin/terms/${term.id}/publish`, {});
      toast.success(`CGV ${term.version} publiées — les nouvelles confirmations utiliseront cette version à sa date d'effet`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const archive = async (term) => {
    try {
      await api.post(`/admin/terms/${term.id}/archive`);
      toast.success(`CGV ${term.version} archivées — les acceptations existantes restent conservées`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!terms) return <div className="py-16 text-center font-heading text-deepgreen" data-testid="terms-loading">Chargement…</div>;

  return (
    <div data-testid="admin-terms-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline-ae">B16 — Documents &amp; CGV</p>
          <h1 className="mt-1 font-heading text-2xl sm:text-3xl text-deepgreen">CGV administrables</h1>
          <p className="mt-1 text-sm text-muted-foreground">Versions brouillon/publiée/archivée, date d'entrée en vigueur et preuves d'acceptation conservées.</p>
        </div>
        <button type="button" onClick={openCreate} className="btn-primary-ae !px-5 !py-2.5 !text-xs" data-testid="terms-create">
          <Plus className="h-4 w-4" /> Nouvelle version
        </button>
      </div>

      {terms.length === 0 ? (
        <div className="mt-6"><EmptyState icon={FileText} title="Aucune version CGV">Créez la première version publiable.</EmptyState></div>
      ) : (
        <div className="card-ae mt-6 overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm" data-testid="terms-table">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-5 py-3">Version</th>
                <th className="px-5 py-3">Entrée en vigueur</th>
                <th className="px-5 py-3">Statut</th>
                <th className="px-5 py-3 text-center">Acceptations</th>
                <th className="px-5 py-3">Empreinte</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {terms.map((term) => (
                <tr key={term.id} className="transition-colors hover:bg-cream/60" data-testid={`terms-row-${term.version}`}>
                  <td className="px-5 py-3.5"><p className="font-semibold text-deepgreen">{term.version}</p><p className="text-xs text-muted-foreground">{term.title}</p></td>
                  <td className="px-5 py-3.5 text-muted-foreground">{term.effective_at || "—"}</td>
                  <td className="px-5 py-3.5"><StatusBadge status={STATUS_LABELS[term.status]} />{term.active && <span className="ml-2 rounded-full bg-sage-light px-2 py-0.5 text-[10px] font-bold text-deepgreen">APPLICABLE</span>}</td>
                  <td className="px-5 py-3.5 text-center font-semibold" data-testid={`terms-acceptance-count-${term.version}`}>{term.acceptance_count}</td>
                  <td className="px-5 py-3.5 font-mono text-xs text-muted-foreground">{term.content_hash.slice(0, 12)}…</td>
                  <td className="px-5 py-3.5"><div className="flex justify-end gap-2">
                    <button type="button" onClick={() => openView(term)} className="rounded-full border border-border px-3 py-1.5 text-[11px] font-bold text-deepgreen hover:bg-sage-light" data-testid={`terms-view-${term.version}`}><Eye className="h-3.5 w-3.5" /></button>
                    {term.status === "brouillon" && <button type="button" onClick={() => openEdit(term)} className="rounded-full border border-border px-3 py-1.5 text-[11px] font-bold text-deepgreen hover:bg-sage-light" data-testid={`terms-edit-${term.version}`}><PencilLine className="h-3.5 w-3.5" /></button>}
                    {term.status === "brouillon" && <button type="button" onClick={() => publish(term)} className="rounded-full bg-deepgreen px-3 py-1.5 text-[11px] font-bold text-white" data-testid={`terms-publish-${term.version}`}><Globe className="mr-1 inline h-3.5 w-3.5" />Publier</button>}
                    {term.status !== "archive" && <button type="button" onClick={() => archive(term)} className="rounded-full border border-border px-3 py-1.5 text-[11px] font-bold text-deepgreen hover:bg-sage-light" data-testid={`terms-archive-${term.version}`}><Archive className="h-3.5 w-3.5" /></button>}
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl bg-cream" data-testid="terms-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-deepgreen">{editing ? `Modifier le brouillon ${editing.version}` : "Nouvelle version CGV"}</DialogTitle>
            <DialogDescription>Une version publiée ou acceptée n'est plus modifiable : créez une nouvelle version.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-3">
              <div><label className="label-ae" htmlFor="term-version">Version</label><input id="term-version" className="input-ae" value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} data-testid="terms-version-input" /></div>
              <div className="sm:col-span-2"><label className="label-ae" htmlFor="term-title">Titre</label><input id="term-title" className="input-ae" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="terms-title-input" /></div>
              <div><label className="label-ae" htmlFor="term-effective">Entrée en vigueur</label><input id="term-effective" type="date" className="input-ae" value={form.effective_at} onChange={(e) => setForm({ ...form, effective_at: e.target.value })} data-testid="terms-effective-at" /></div>
            </div>
            <div><label className="label-ae" htmlFor="term-content">Contenu</label><textarea id="term-content" className="input-ae min-h-56 font-mono text-xs" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="terms-content" /></div>
            <button type="button" onClick={submit} disabled={form.version.length < 2 || form.title.length < 3 || form.content.length < 10} className="btn-primary-ae modal-sticky-btn w-full disabled:opacity-50" data-testid="terms-submit">{editing ? "Enregistrer le brouillon" : "Créer le brouillon"}</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(viewing)} onOpenChange={() => setViewing(null)}>
        <DialogContent className="max-w-3xl bg-cream" data-testid="terms-view-dialog">
          {viewing && <>
            <DialogHeader>
              <DialogTitle className="font-heading text-deepgreen">CGV {viewing.version}</DialogTitle>
              <DialogDescription>{viewing.acceptance_count} acceptation(s) conservée(s) — empreinte {viewing.content_hash.slice(0, 16)}…</DialogDescription>
            </DialogHeader>
            <div className="max-h-64 overflow-y-auto rounded-lg bg-white p-4 text-sm whitespace-pre-wrap" data-testid="terms-view-content">{viewing.content}</div>
            <div className="mt-4 max-h-48 overflow-y-auto" data-testid="terms-acceptances">
              {acceptances.map((a) => <p key={a.id} className="border-b border-border py-2 text-xs text-muted-foreground">Réservation {a.reservation_id} — {a.accepted_at} — hash {a.content_hash?.slice(0, 12)}…</p>)}
              {acceptances.length === 0 && <p className="text-sm text-muted-foreground">Aucune acceptation enregistrée pour cette version.</p>}
            </div>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
